import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchPerson, updatePerson, fetchTasksForPerson, fetchPeople } from '../api/people';
import { fetchThemes, updateTask } from '../api/projects';
import type { Person } from '../types/people';
import type { Task, Theme, TaskStatus, TaskPriority } from '../types/project';
import KanbanBoard from '../components/KanbanBoard';
import TaskList from '../components/TaskList';
import { TASK_STATUSES, STATUS_LABEL } from '../utils/taskStatus';

type ViewMode = 'list' | 'board';
type DueDateFilter = 'all' | 'overdue' | 'no_date' | 'has_date';

export default function PersonDetailPage() {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const [person, setPerson] = useState<Person | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', avatar_url: '', email: '' });
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [themeFilter, setThemeFilter] = useState<string>('ALL');
  const [subThemeFilter, setSubThemeFilter] = useState<string>('ALL');
  const [dueDateFilter, setDueDateFilter] = useState<DueDateFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'ALL'>('ALL');

  const load = useCallback(() => {
    if (!personId) return;
    setLoading(true);
    Promise.all([fetchPerson(personId), fetchTasksForPerson(personId), fetchThemes(), fetchPeople()])
      .then(([p, t, th, pe]) => {
        setPerson(p);
        setTasks(t);
        setThemes(th);
        setPeople(pe);
        setForm({
          name: p.name,
          avatar_url: p.avatar_url ?? '',
          email: p.email ?? '',
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [personId]);

  const reloadTasks = useCallback(async () => {
    if (!personId) return;
    try {
      const t = await fetchTasksForPerson(personId);
      setTasks(t);
    } catch (e) {
      // Avoid interrupting UX; keep existing tasks until the next successful refresh.
    }
  }, [personId]);

  useEffect(() => {
    load();
  }, [load]);

  // Refetch when returning to this view so deleted/updated tasks elsewhere
  // don't linger in stale client state.
  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      reloadTasks();
    };

    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    const intervalId = window.setInterval(() => {
      // Periodic refresh helps when multiple tabs are open and focus
      // events don't fire reliably.
      if (document.visibilityState === 'visible') reloadTasks();
    }, 20000);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.clearInterval(intervalId);
    };
  }, [reloadTasks]);

  const handleAvatarFile = useCallback(
    (file: File) => {
      if (!person) return;
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : null;
        if (!dataUrl) return;
        setAvatarPreview(dataUrl);
        setAvatarSaving(true);
        try {
          await updatePerson(person.id, { avatar_url: dataUrl });
          setPerson((prev) => (prev ? { ...prev, avatar_url: dataUrl } : prev));
          setForm((f) => ({ ...f, avatar_url: dataUrl }));
          setAvatarModalOpen(false);
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Failed to update avatar');
        } finally {
          setAvatarSaving(false);
        }
      };
      reader.readAsDataURL(file);
    },
    [person]
  );

  const handleAvatarDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleAvatarFile(file);
      }
    },
    [handleAvatarFile]
  );

  const handleAvatarInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleAvatarFile(file);
      }
    },
    [handleAvatarFile]
  );

  const handleSave = useCallback(async () => {
    if (!person) return;
    try {
      await updatePerson(person.id, {
        name: form.name.trim() || person.name,
        avatar_url: form.avatar_url.trim() || null,
        email: form.email.trim() || null,
      });
      setPerson((prev) =>
        prev
          ? {
              ...prev,
              name: form.name.trim() || prev.name,
              avatar_url: form.avatar_url.trim() || null,
              email: form.email.trim() || null,
            }
          : null
      );
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    }
  }, [person, form]);

  const handleStatusChange = useCallback(
    async (taskId: string, status: TaskStatus) => {
      try {
        await updateTask(taskId, { status });
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status } : t)));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update');
      }
    },
    []
  );

  const handlePriorityChange = useCallback(
    async (taskId: string, priority: TaskPriority) => {
      try {
        await updateTask(taskId, { priority });
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, priority } : t)));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update');
      }
    },
    []
  );

  const handleAssigneesChange = useCallback(
    async (taskId: string, assigneeIds: string[]) => {
      try {
        await updateTask(taskId, { assignee_ids: assigneeIds });
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  assignee_ids: assigneeIds,
                  assignee: assigneeIds.length > 0 ? assigneeIds[0] : null,
                }
              : t
          )
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update');
      }
    },
    []
  );

  const rootThemes = useMemo(() => themes.filter((t) => !t.parent_theme_id), [themes]);
  const subThemes = useMemo(() => themes.filter((t) => t.parent_theme_id), [themes]);
  const subThemesForSelectedTheme = useMemo(
    () => (themeFilter === 'ALL' ? subThemes : subThemes.filter((s) => s.parent_theme_id === themeFilter)),
    [subThemes, themeFilter]
  );

  const filteredTasks = useMemo(() => {
    let result = [...tasks];
    if (themeFilter !== 'ALL') {
      const themeIds = new Set([
        themeFilter,
        ...subThemes.filter((s) => s.parent_theme_id === themeFilter).map((s) => s.id),
      ]);
      result = result.filter((t) => themeIds.has(t.theme_id));
    }
    if (subThemeFilter !== 'ALL') {
      result = result.filter((t) => t.theme_id === subThemeFilter);
    }
    if (dueDateFilter === 'overdue') {
      const today = new Date().toISOString().slice(0, 10);
      result = result.filter((t) => t.due_date != null && t.due_date < today);
    } else if (dueDateFilter === 'no_date') {
      result = result.filter((t) => !t.due_date);
    } else if (dueDateFilter === 'has_date') {
      result = result.filter((t) => !!t.due_date);
    }
    if (priorityFilter !== 'ALL') {
      result = result.filter((t) => t.priority === priorityFilter);
    }
    if (statusFilter !== 'ALL') {
      result = result.filter((t) => t.status === statusFilter);
    }
    return result;
  }, [tasks, themes, subThemes, themeFilter, subThemeFilter, dueDateFilter, priorityFilter, statusFilter]);

  if (loading || !person) {
    return <div>{loading ? 'Loading…' : 'Person not found'}</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;
  }

  return (
    <>
      <nav style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
        <Link to="/people/founders" style={{ color: 'var(--color-blue-dark)' }}>
          People
        </Link>
        {' / '}
        <Link to={person.person_type === 'founder' ? '/people/founders' : '/people/external'} style={{ color: 'var(--color-blue-dark)' }}>
          {person.person_type === 'founder' ? 'Founders' : 'External'}
        </Link>
        {' / '}
        <span>{person.name}</span>
      </nav>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap', marginBottom: '32px' }}>
        <button
          type="button"
          onClick={() => editing && setAvatarModalOpen(true)}
          style={{
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: editing ? 'pointer' : 'default',
          }}
          aria-label={editing ? 'Change profile picture' : undefined}
        >
          <img
            src={person.avatar_url ?? undefined}
            alt=""
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              objectFit: 'cover',
              background: 'var(--color-todo-bg)',
            }}
          />
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          {editing ? (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleSave}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--color-blue-dark)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForm({ name: person.name, avatar_url: person.avatar_url ?? '', email: person.email ?? '' });
                    setEditing(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 4px' }}>{person.name}</h1>
              {person.email && (
                <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>{person.email}</div>
              )}
              <button
                type="button"
                onClick={() => setEditing(true)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                }}
              >
                Edit profile
              </button>
            </>
          )}
        </div>
      </div>

      <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Tasks assigned</h2>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: viewMode === 'list' ? 'var(--color-blue-dark)' : 'white',
              color: viewMode === 'list' ? 'white' : 'var(--color-text-primary)',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setViewMode('board')}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: viewMode === 'board' ? 'var(--color-blue-dark)' : 'white',
              color: viewMode === 'board' ? 'white' : 'var(--color-text-primary)',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Board
          </button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Theme:
          <select
            value={themeFilter}
            onChange={(e) => {
              const next = e.target.value;
              setThemeFilter(next);
              if (next !== 'ALL' && subThemeFilter !== 'ALL') {
                const allowed = subThemes.filter((s) => s.parent_theme_id === next).map((s) => s.id);
                if (!allowed.includes(subThemeFilter)) setSubThemeFilter('ALL');
              }
            }}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="ALL">All</option>
            {rootThemes.map((th) => (
              <option key={th.id} value={th.id}>{th.name}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Sub-theme:
          <select
            value={subThemeFilter}
            onChange={(e) => {
              const next = e.target.value;
              setSubThemeFilter(next);
              if (next === 'ALL') {
                return;
              }
              const selectedSub = subThemes.find((s) => s.id === next);
              if (selectedSub?.parent_theme_id) {
                setThemeFilter(selectedSub.parent_theme_id);
              }
            }}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="ALL">All</option>
            {subThemesForSelectedTheme.map((th) => (
              <option key={th.id} value={th.id}>{th.name}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Due date:
          <select
            value={dueDateFilter}
            onChange={(e) => setDueDateFilter(e.target.value as DueDateFilter)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="all">All</option>
            <option value="overdue">Overdue</option>
            <option value="no_date">No date</option>
            <option value="has_date">Has date</option>
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Priority:
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | 'ALL')}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="ALL">All</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Status:
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TaskStatus | 'ALL')}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="ALL">All</option>
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </label>
      </div>

      {tasks.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>No tasks assigned to this person.</p>
      ) : filteredTasks.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>No tasks match the current filters.</p>
      ) : viewMode === 'list' ? (
        <TaskList
          tasks={filteredTasks}
          people={people}
          onTaskClick={(task) => navigate(`/projects/task/${task.id}`)}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onAssigneesChange={handleAssigneesChange}
          showAddButton={false}
        />
      ) : (
        <KanbanBoard
          tasks={filteredTasks}
          people={people}
          onTaskClick={(task) => navigate(`/projects/task/${task.id}`)}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onAssigneesChange={handleAssigneesChange}
          showAddCard={false}
        />
      )}
      {avatarModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => !avatarSaving && setAvatarModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              width: '100%',
              maxWidth: 420,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Update profile picture</h2>
            <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: '0 0 16px' }}>
              Drag and drop a new image here, or click to choose a file.
            </p>
            <div
              onDrop={handleAvatarDrop}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{
                border: '2px dashed var(--color-border)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => {
                const input = document.getElementById('avatar-file-input') as HTMLInputElement | null;
                input?.click();
              }}
            >
              <input
                id="avatar-file-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarInputChange}
              />
              <div style={{ marginBottom: '12px' }}>
                <img
                  src={avatarPreview ?? person.avatar_url ?? undefined}
                  alt=""
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    background: 'var(--color-todo-bg)',
                  }}
                />
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                Drop a PNG or JPG file. The picture will update as soon as it uploads.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
